<script setup lang="ts">
import type { PropType } from "vue";

defineProps({
  navigation: {
    type: Array as PropType<Array<{ name: string; href: string }>>,
    required: true,
  },
  mobileMenuOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ "toggle-mobile-menu": [] }>();

// VueFire best practice: call useCurrentUser() once at component setup level.
// Returns `undefined` while Firebase Auth is initialising, `null` when signed
// out, and a User object when authenticated.  Never call inside computed or
// lifecycle hooks.
const user = useCurrentUser();
const userStore = useUserStore();

// Controls the loading spinner when navigating to the dashboard
const dashboardLoaded = useState<boolean>("dashboard:loaded", () => false);

// clientMounted is false during SSR AND during client hydration (before onMounted
// fires). This ensures authPending=false on BOTH sides → Login button renders on
// both → no hydration mismatch. After mount, Firebase state can update freely.
const clientMounted = ref(false);

onMounted(() => {
  clientMounted.value = true;
  dashboardLoaded.value = true;
});

// Show loading skeleton only after mount — while clientMounted=false both SSR
// and client produce the same "not authenticated" output (hydration-safe).
const authPending = computed(() => {
  if (!clientMounted.value) return false;
  return user.value === undefined;
});

// Use the same gate as the auth middleware (userStore.signedIn) so users
// mid-2FA or before the store hydrates don't see a Dashboard button that
// immediately redirects them back out.
const isAuthenticated = computed(() => !!userStore.signedIn);
</script>

<template>
  <nav
    class="flex items-center justify-between px-4 py-3 mx-auto max-w-7xl sm:px-6"
    aria-label="Global">

    <!-- Logo -->
    <NuxtLink
      to="/"
      class="flex items-center shrink-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
      <span class="sr-only">VimiCrypto – Home</span>
      <VimiLogo class="w-auto h-14" />
    </NuxtLink>

    <!-- Desktop nav links -->
    <div class="hidden md:flex items-center gap-0.5">
      <a
        v-for="item in navigation"
        :key="item.name"
        :href="item.href"
        class="px-4 py-2 text-base font-medium text-muted-foreground rounded-lg hover:text-foreground hover:bg-accent transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {{ item.name }}
      </a>
    </div>

    <!-- Right-side actions -->
    <div class="flex items-center gap-2">

      <!-- Theme toggle — wrapped in ClientOnly because UColorModeButton uses a
           Vue Teleport internally; rendering it on the server causes a
           harmless but noisy hydration node-mismatch warning. -->
      <ClientOnly>
        <UColorModeButton
          size="sm"
          variant="ghost"
          class="hidden sm:flex rounded-lg hover:bg-accent transition-colors duration-200" />
      </ClientOnly>

      <!-- Auth pending: skeleton placeholders so layout doesn't jump -->
      <template v-if="authPending">
        <div class="hidden md:block h-9 w-32 rounded-lg bg-muted/60 animate-pulse" />
        <div class="md:hidden h-9 w-20 rounded-lg bg-muted/60 animate-pulse" />
      </template>

      <!-- Authenticated → Dashboard button -->
      <template v-else-if="isAuthenticated">
        <!-- Mobile -->
        <UButton
          to="/system"
          trailing-icon="i-material-symbols-arrow-right-alt-rounded"
          :loading="!dashboardLoaded"
          size="sm"
          class="md:hidden bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg font-semibold transition-all duration-200"
          @click="dashboardLoaded = false">
          Dashboard
        </UButton>
        <!-- Desktop -->
        <UButton
          to="/system"
          trailing-icon="i-material-symbols-arrow-right-alt-rounded"
          :loading="!dashboardLoaded"
          size="md"
          class="hidden md:flex bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-5 font-semibold transition-all duration-200"
          @click="dashboardLoaded = false">
          Go to Dashboard
        </UButton>
      </template>

      <!-- Not authenticated → Login link -->
      <template v-else>
        <!-- Mobile -->
        <NuxtLink
          to="/login"
          class="md:hidden inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          Log in
          <UIcon name="i-material-symbols-arrow-right-alt-rounded" class="w-4 h-4" />
        </NuxtLink>
        <!-- Desktop -->
        <NuxtLink
          to="/login"
          class="hidden md:inline-flex items-center gap-2 px-6 py-2.5 text-base font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
          Log in
          <UIcon name="i-material-symbols-arrow-right-alt-rounded" class="w-5 h-5" />
        </NuxtLink>
      </template>

      <!-- Mobile hamburger toggle — plain <button> replaces HeadlessUI
           <PopoverButton> to avoid the internal PortalWrapper teleport that
           causes SSR hydration mismatches.  State lives in ModernHero.vue. -->
      <div class="flex md:hidden">
        <button
          type="button"
          class="inline-flex items-center justify-center p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          :aria-expanded="mobileMenuOpen"
          :aria-label="mobileMenuOpen ? 'Close main menu' : 'Open main menu'"
          @click="emit('toggle-mobile-menu')">
          <UIcon
            :name="mobileMenuOpen ? 'i-material-symbols-close' : 'i-material-symbols-menu'"
            class="w-6 h-6"
            aria-hidden="true" />
        </button>
      </div>
    </div>
  </nav>
</template>

<style lang="scss" scoped></style>
